import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Save, X, Plus } from 'lucide-react';
import { ComponentLifetime, DirectionalLifetime } from './types';
import { BuildComponentType, CardinalDirection } from '../../../types/Enums';
import { translate } from '../../../types/EnumTranslations';

interface LifetimeEditModalProps {
    show: boolean;
    lifetime: ComponentLifetime | undefined;
    componentType: BuildComponentType;
    onSave: (lifetime: ComponentLifetime) => Promise<void>;
    onCancel: () => void;
    saving: boolean;
}

const LifetimeEditModal: React.FC<LifetimeEditModalProps> = ({
    show,
    lifetime,
    componentType,
    onSave,
    onCancel,
    saving
}) => {
    const [lifetimeForm, setLifetimeForm] = useState<{
        expectedLifetimeYears: number;
        replacementCost: number;
        costUnit: 'per_sqm' | 'per_unit';
        directionalLifetimes: DirectionalLifetime[];
    }>({
        expectedLifetimeYears: 0,
        replacementCost: 0,
        costUnit: 'per_sqm',
        directionalLifetimes: []
    });

    useEffect(() => {
        if (show) {
            if (lifetime) {
                setLifetimeForm({
                    expectedLifetimeYears: lifetime.expectedLifetimeYears || 0,
                    replacementCost: lifetime.replacementCost || 0,
                    costUnit: lifetime.costUnit || 'per_sqm',
                    directionalLifetimes: lifetime.directionalLifetimes || []
                });
            } else {
                setLifetimeForm({
                    expectedLifetimeYears: 0,
                    replacementCost: 0,
                    costUnit: 'per_sqm',
                    directionalLifetimes: []
                });
            }
        }
    }, [lifetime, show]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!lifetimeForm.expectedLifetimeYears || lifetimeForm.expectedLifetimeYears <= 0) {
            alert('Förväntad livslängd måste vara större än 0');
            return;
        }
        if (lifetimeForm.replacementCost < 0) {
            alert('Utbyteskostnad kan inte vara negativ');
            return;
        }

        await onSave(lifetimeForm);
    };

    const addDirectionalLifetime = (): void => {
        const usedDirections = lifetimeForm.directionalLifetimes.map(dl => dl.direction);
        const availableDirections = Object.values(CardinalDirection).filter(
            direction => !usedDirections.includes(direction)
        );

        if (availableDirections.length > 0) {
            setLifetimeForm(prev => ({
                ...prev,
                directionalLifetimes: [
                    ...prev.directionalLifetimes,
                    {
                        direction: availableDirections[0],
                        expectedLifetimeYears: prev.expectedLifetimeYears,
                        replacementCost: undefined
                    }
                ]
            }));
        }
    };

    const updateDirectionalLifetime = (index: number, field: keyof DirectionalLifetime, value: any): void => {
        setLifetimeForm(prev => ({
            ...prev,
            directionalLifetimes: prev.directionalLifetimes.map((dl, i) =>
                i === index ? { ...dl, [field]: value } : dl
            )
        }));
    };

    const removeDirectionalLifetime = (index: number): void => {
        setLifetimeForm(prev => ({
            ...prev,
            directionalLifetimes: prev.directionalLifetimes.filter((_, i) => i !== index)
        }));
    };

    return (
        <Modal show={show} onHide={onCancel} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    Beräknad livslängd - {translate(componentType)}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <form onSubmit={handleSubmit}>
                    {/* Standard Lifetime Fields */}
                    <div className="row g-3 mb-4">
                        <div className="col-md-4">
                            <label className="form-label fw-medium">Standard livslängd (år)</label>
                            <input
                                type="number"
                                min="1"
                                step="1"
                                className="form-control"
                                value={lifetimeForm.expectedLifetimeYears}
                                onChange={(e) =>
                                    setLifetimeForm(prev => ({ ...prev, expectedLifetimeYears: parseInt(e.target.value, 10) || 0 }))
                                }
                                placeholder="T.ex. 25"
                                required
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-medium">Utbyteskostnad</label>
                            <input
                                type="number"
                                min="0"
                                step="100"
                                className="form-control"
                                value={lifetimeForm.replacementCost}
                                onChange={(e) =>
                                    setLifetimeForm(prev => ({ ...prev, replacementCost: parseFloat(e.target.value) || 0 }))
                                }
                                placeholder="T.ex. 2500"
                                required
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-medium">Kostnadsenhet</label>
                            <select
                                className="form-select"
                                value={lifetimeForm.costUnit}
                                onChange={(e) =>
                                    setLifetimeForm(prev => ({ ...prev, costUnit: e.target.value as 'per_sqm' | 'per_unit' }))
                                }
                            >
                                <option value="per_sqm">kr/kvm</option>
                                <option value="per_unit">kr/st</option>
                            </select>
                        </div>
                    </div>

                    {/* Directional Lifetimes */}
                    <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0 fw-semibold" style={{color: '#495057'}}>Väderstrecksberoende livslängd (valfritt)</h6>
                            {Object.values(CardinalDirection).filter(dir =>
                                !lifetimeForm.directionalLifetimes.some(dl => dl.direction === dir)
                            ).length > 0 && (
                                <button
                                    onClick={addDirectionalLifetime}
                                    className="btn btn-outline-secondary btn-sm"
                                    type="button"
                                >
                                    <Plus size={14} className="me-1" />
                                    Lägg till väderstreck
                                </button>
                            )}
                        </div>

                        {lifetimeForm.directionalLifetimes.length === 0 && (
                            <div className="text-muted fst-italic small mb-3">
                                Använder standard livslängd ({lifetimeForm.expectedLifetimeYears} år) för alla väderstreck
                            </div>
                        )}

                        {lifetimeForm.directionalLifetimes.map((directionalLifetime, index) => {
                            const usedDirections = lifetimeForm.directionalLifetimes.map(dl => dl.direction);
                            const availableDirections = Object.values(CardinalDirection).filter(
                                direction => !usedDirections.includes(direction) || direction === directionalLifetime.direction
                            );

                            return (
                                <div key={index} className="card mb-2" style={{background: '#fafbfc', border: '1px solid #e9ecef'}}>
                                    <div className="card-body p-3">
                                        <div className="row g-2 align-items-center">
                                            <div className="col-md-3">
                                                <label className="form-label small fw-medium mb-1">Väderstreck</label>
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={directionalLifetime.direction}
                                                    onChange={(e) => updateDirectionalLifetime(index, 'direction', e.target.value as CardinalDirection)}
                                                >
                                                    {availableDirections.map(direction => (
                                                        <option key={direction} value={direction}>
                                                            {translate(direction)}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-md-3">
                                                <label className="form-label small fw-medium mb-1">Livslängd (år)</label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    step="1"
                                                    className="form-control form-control-sm"
                                                    value={directionalLifetime.expectedLifetimeYears}
                                                    onChange={(e) => updateDirectionalLifetime(index, 'expectedLifetimeYears', parseInt(e.target.value) || 1)}
                                                />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label small fw-medium mb-1">Byteskostnad (valfritt)</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="100"
                                                    className="form-control form-control-sm"
                                                    value={directionalLifetime.replacementCost || ''}
                                                    onChange={(e) => updateDirectionalLifetime(index, 'replacementCost', parseFloat(e.target.value) || undefined)}
                                                    placeholder="Använd standard"
                                                />
                                            </div>
                                            <div className="col-md-2 d-flex align-items-end">
                                                <button
                                                    onClick={() => removeDirectionalLifetime(index)}
                                                    className="btn btn-outline-danger btn-sm"
                                                    type="button"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {lifetimeForm.directionalLifetimes.length > 0 && (
                            <div className="alert alert-info small mt-2 mb-0">
                                <strong>Info:</strong> Väderstreck utan specificerad livslängd använder standard ({lifetimeForm.expectedLifetimeYears} år).
                                Tomma byteskostnader använder standard ({lifetimeForm.replacementCost} kr).
                            </div>
                        )}
                    </div>
                </form>
            </Modal.Body>
            <Modal.Footer>
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={onCancel}
                    disabled={saving}
                >
                    <X size={16} className="me-1" />
                    Avbryt
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={saving}
                >
                    {saving ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Sparar...
                        </>
                    ) : (
                        <>
                            <Save size={16} className="me-1" />
                            Spara
                        </>
                    )}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default LifetimeEditModal;

